import type { CdkDragDrop } from '@angular/cdk/drag-drop';
import { DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgTemplateOutlet } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  model,
  signal,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { countBy, differenceBy, orderBy, uniqBy } from 'lodash';
import { OnyxTooltipDirective } from '../../../../directives/onyx-tooltip.directive';
import type { OnyxOptional } from '../../../../interfaces';
import { I18N_NAMESPACE } from '../../../../internal/constants';
import { OnyxFilterPipe } from '../../../../pipes/onyx-filter.pipe';
import { OnyxHighlightPipe } from '../../../../pipes/onyx-highlight.pipe';
import { OnyxIconButtonComponent } from '../../../buttons/onyx-icon-button/onyx-icon-button.component';
import { OnyxIconComponent } from '../../../icons/onyx-icon/onyx-icon.component';
import { OnyxSearchComponent } from '../../../search/onyx-search/onyx-search.component';
import type { OnyxTableColumn, OnyxTableView } from '../../interfaces';

@Component({
  selector: 'onyx-table-columns-picker',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    OnyxSearchComponent,
    OnyxFilterPipe,
    OnyxHighlightPipe,
    OnyxIconButtonComponent,
    OnyxIconComponent,
    DragDropModule,
    NgTemplateOutlet,
    OnyxTooltipDirective,
  ],
  templateUrl: './onyx-table-columns-picker.component.html',
  styleUrl: './onyx-table-columns-picker.component.scss',
  host: { '[class]': `variant()` },
})
export class OnyxTableColumnsPickerComponent<T> implements OnInit {
  private readonly injector = inject(Injector);

  public readonly columns = input.required<OnyxTableColumn<T>[]>();
  public readonly value = model.required<string[]>();
  public readonly defaultView = input<OnyxTableView>();
  public readonly defaultViews = input<OnyxTableView[]>();
  public readonly isDefault = input<OnyxOptional<boolean>>();
  public readonly name = input<string>();
  public readonly variant = input<'modal' | 'dropdown'>('dropdown');

  protected readonly I18N = `${I18N_NAMESPACE}.table.viewModal`;

  protected visibleColumns = signal<OnyxTableColumn<any>[]>([]);
  protected query = signal('');
  protected hiddenColumns = computed(() =>
    orderBy(
      differenceBy(this.columns(), this.visibleColumns(), ({ id }) => id),
      ({ name }) => name,
    ),
  );

  private requiredColumnsCount = computed(
    () => countBy(this.visibleColumns(), ({ required }) => required)['true'],
  );

  public ngOnInit(): void {
    const viewColumns = this.columns().filter((column) =>
      this.value().includes(column.id),
    );
    this.visibleColumns.set(
      orderBy(viewColumns, (column) => this.value().indexOf(column.id)),
    );

    effect(
      () => this.value.set(this.visibleColumns().map((column) => column.id)),
      { injector: this.injector },
    );
  }

  protected dropPredicate(index: number): boolean {
    return index > this.requiredColumnsCount() - 1;
  }

  protected drop(event: CdkDragDrop<unknown>): void {
    this.visibleColumns.update((visibleColumns) => {
      const columns = [...visibleColumns];
      moveItemInArray(columns, event.previousIndex, event.currentIndex);
      return columns;
    });
  }

  protected hideColumn(id: string): void {
    this.visibleColumns.update((columns) =>
      columns.filter((column) => column.id !== id),
    );
    this.query.set('');
  }

  protected showColumn(column: OnyxTableColumn<any>): void {
    this.visibleColumns.update((columns) => uniqBy([...columns, column], 'id'));
    this.query.set('');
  }

  protected restoreDefaults(): void {
    const columns = this.columns();
    const defaultView = this.defaultView();

    const mapIdsToColumns = (ids: string[]) =>
      ids
        .map((id) => columns.find((column) => column.id === id))
        .filter((column) => !!column);

    if (defaultView) {
      this.query.set('');
      this.visibleColumns.set(mapIdsToColumns(defaultView.value.columns));
      return;
    }

    if (this.isDefault() && this.name()) {
      const view = this.defaultViews()?.find(
        (view) => view.name === this.name(),
      );
      if (view) this.visibleColumns.set(mapIdsToColumns(view.value.columns));
    } else if (!this.isDefault()) {
      this.visibleColumns.set(columns.filter((column) => column.required));
    }
  }
}
